import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { goalPlanRequestSchema, type GoalPlanRequest, type HabitInput, type PlanProposal } from "@habitos/shared";
import { api } from "../lib/api";
import { PlanReview, Thinking } from "../components/PlanReview";

export function GoalNew() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [goal, setGoal] = useState<GoalPlanRequest | null>(null);
  const [proposal, setProposal] = useState<PlanProposal | null>(null);
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function propose(request: GoalPlanRequest) {
    setError(null);
    setThinking(true);
    setProposal(null);
    try {
      setProposal(await api.proposePlan(request));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setThinking(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    const parsed = goalPlanRequestSchema.safeParse({
      title: text("title") ?? "",
      details: text("details"),
      targetDate: text("targetDate"),
      availability: text("availability"),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    setGoal(parsed.data);
    void propose(parsed.data);
  }

  async function accept(habits: HabitInput[]) {
    if (!goal || !proposal) return;
    setSaving(true);
    try {
      const created = await api.createGoal({ ...goal, planSummary: proposal.summary, habits });
      await Promise.all([
        client.invalidateQueries({ queryKey: ["goals"] }),
        client.invalidateQueries({ queryKey: ["habits"] }),
      ]);
      navigate(`/metas/${created.id}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <>
      <header className="page-header row start">
        <button className="icon-btn" aria-label="Volver" onClick={() => (proposal ? setProposal(null) : navigate(-1))}>‹</button>
        <h1>Nueva meta</h1>
      </header>

      {thinking ? (
        <Thinking text="Armando un plan con pasos pequeños…" />
      ) : proposal ? (
        <PlanReview
          proposal={proposal}
          acceptLabel="Guardar en mi calendario"
          busy={saving}
          onAccept={accept}
          onRetry={() => goal && void propose(goal)}
        />
      ) : (
        <form onSubmit={onSubmit} className="card form">
          <label>
            ¿Qué quieres lograr?
            <input name="title" required maxLength={200} defaultValue={goal?.title} placeholder="Correr 5 km sin parar" />
          </label>
          <label>
            Cuéntame más <span className="muted">(opcional)</span>
            <textarea
              name="details"
              className="textarea"
              maxLength={1000}
              defaultValue={goal?.details ?? ""}
              placeholder="Por qué te importa, desde dónde empiezas…"
            />
          </label>
          <label>
            ¿Cuándo tienes tiempo? <span className="muted">(opcional)</span>
            <textarea
              name="availability"
              className="textarea"
              maxLength={500}
              defaultValue={goal?.availability ?? ""}
              placeholder="Mañanas después de dejar a los niños; las noches son difíciles"
            />
          </label>
          <label>
            ¿Para cuándo? <span className="muted">(opcional)</span>
            <input name="targetDate" type="date" defaultValue={goal?.targetDate ?? ""} />
          </label>
          {error && <p className="error">{error}</p>}
          <button className="btn primary">Crear plan con IA</button>
        </form>
      )}
      {error && (thinking || proposal) && <p className="error">{error}</p>}
    </>
  );
}
